import AuthLink from "../../components/common/AuthLink";
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styles from './HomePage.module.css';

const useAutoScroll = (ref, intervalMs) => {
  const timerRef = useRef(null);
  const indexRef = useRef(0);
  const isAutoScrolling = useRef(false);

  const startScroll = useCallback(() => {
    const container = ref.current;
    if (!container) return;
    timerRef.current = setInterval(() => {
      const children = container.children;
      const totalCards = children.length;
      if (totalCards === 0) return;
      indexRef.current = (indexRef.current + 1) % totalCards;
      const target = children[indexRef.current];
      isAutoScrolling.current = true;
      const offset = target.offsetLeft - container.offsetLeft
        - (container.clientWidth / 2) + (target.offsetWidth / 2);
      container.scrollTo({ left: offset, behavior: 'smooth' });
      setTimeout(() => { isAutoScrolling.current = false; }, 600);
    }, intervalMs);
  }, [ref, intervalMs]);

  const stopScroll = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    isAutoScrolling.current = false;
  }, []);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleMediaChange = (e) => {
      if (e.matches) {
        indexRef.current = 0;
        startScroll();
      } else {
        stopScroll();
      }
    };

    if (mediaQuery.matches) startScroll();
    mediaQuery.addEventListener('change', handleMediaChange);

    const handleTouchStart = () => stopScroll();
    const handleTouchEnd = () => {
      if (mediaQuery.matches) {
        setTimeout(() => startScroll(), 2000);
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      stopScroll();
      mediaQuery.removeEventListener('change', handleMediaChange);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [ref, startScroll, stopScroll]);

  return isAutoScrolling;
};

const HomePage = () => {
  const testimonialsRef = useRef(null);
  const stepsRef = useRef(null);

  useAutoScroll(testimonialsRef, 4000);
  useAutoScroll(stepsRef, 5000);

  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const container = stepsRef.current;
    if (!container) return;

    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const containerRect = container.getBoundingClientRect();
        const centerX = containerRect.left + containerRect.width / 2;
        const children = container.children;
        let closestIdx = 0;
        let closestDist = Infinity;
        for (let i = 0; i < children.length; i++) {
          const childRect = children[i].getBoundingClientRect();
          const childCenter = childRect.left + childRect.width / 2;
          const dist = Math.abs(childCenter - centerX);
          if (dist < closestDist) {
            closestDist = dist;
            closestIdx = i;
          }
        }
        setActiveStep((prev) => prev !== closestIdx ? closestIdx : prev);
        ticking = false;
      });
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Helper to get category color class
  const getCategoryClass = (category) => {
    const categoryMap = {
      'Weight Care': styles.categoryWeightCare,
      'Sexual Wellness': styles.categorySexualWellness,
      'Hormone Support': styles.categoryHormoneSupport,
      'Hair': styles.categoryHair,
      'Skin Care': styles.categorySkinCare,
    };
    return categoryMap[category] || styles.categoryDefault;
  };

  const categories = [
    { name: 'Hair Care', slug: 'hair-care' },
    { name: 'Skin Care', slug: 'skin-care' },
    { name: 'Sexual Wellness', slug: 'sexual-wellness' },
    { name: 'Weight Loss', slug: 'weight-loss' },
    { name: 'Hormone Support', slug: 'hormone-support' },
    { name: 'Anti-Aging', slug: 'anti-aging' }
  ];

  const concerns = [
    { title: 'Hair Loss', slug: 'hair-loss' },
    { title: 'Skin Health', slug: 'skin-health' },
    { title: 'Weight Management', slug: 'weight-management' },
    { title: 'Sexual Health', slug: 'sexual-health' },
    { title: 'Hormone Balance', slug: 'hormone-balance' }
  ];

  const treatments = [
    { name: 'Semaglutide FastSL', category: 'Weight Care', desc: 'A sublingual Semaglutide formula designed for improved absorption and convenient, needle-free weight-loss support.', slug: 'semaglutide-fastsl', image: '/images/landing-page/personalized-treatments-section/semaglutide-fastsl.webp' },
    { name: 'Wegovy', category: 'Weight Care', desc: 'FDA-approved Semaglutide injection for chronic weight management, designed to reduce appetite, improve metabolic function, and support long-term weight loss.', slug: 'wegovy', image: '/images/landing-page/personalized-treatments-section/wegovy.webp' },
    { name: 'Tadalafil', category: 'Sexual Wellness', desc: 'A fast-acting Tadalafil formula with Oxytocin to support erectile performance while promoting intimacy and relaxation.', slug: 'tadalafil', image: '/images/landing-page/personalized-treatments-section/tadalafil.webp' },
    { name: 'Enclomiphene', category: 'Hormone Support', desc: 'A once-daily oral therapy that boosts natural testosterone production while maintaining fertility, energy, and libido.', slug: 'enclomiphene', image: '/images/landing-page/personalized-treatments-section/enclomiphene.webp' },
    { name: 'Metformin XR', category: 'Hormone Support', desc: 'Extended-release Metformin designed to support healthy insulin response, regulate cycles, and improve metabolic balance in PCOS.', slug: 'metformin-xr', image: '/images/landing-page/personalized-treatments-section/metformin-xr.webp' },
    { name: 'Compounded Semaglutide', category: 'Weight Care', desc: 'A compounded injectable blend of Semaglutide with Vitamin B12 to support appetite control, weight reduction, and metabolic health.', slug: 'compounded-semaglutide', image: '/images/landing-page/personalized-treatments-section/compounded-semaglutide.webp' },
    { name: 'Oxytocin Nasal', category: 'Sexual Wellness', desc: 'A nasal spray formulation of Oxytocin designed to promote bonding, intimacy, and emotional connection.', slug: 'oxytocin-nasal', image: '/images/landing-page/personalized-treatments-section/oxytocin-nasal.webp' },
    { name: 'Sildenafil', category: 'Sexual Wellness', desc: 'Fast-dissolving formula that combines Sildenafil with Oxytocin to support erections while enhancing sexual connection and arousal.', slug: 'sildenafil', image: '/images/landing-page/personalized-treatments-section/sildenafil.webp' },
    { name: 'Finasteride Combo', category: 'Hair', desc: 'A once-daily oral formula combining Finasteride with Biotin to help reduce hair thinning and support healthy hair growth.', slug: 'finasteride-combo', image: '/images/landing-page/personalized-treatments-section/finasteride-combo.webp' },
    { name: 'Minoxidil Topical', category: 'Hair', desc: 'A multi-action topical treatment targeting scalp inflammation, follicle stimulation, and DHT reduction to promote stronger hair regrowth.', slug: 'minoxidil-topical', image: '/images/landing-page/personalized-treatments-section/minoxidil-topical.webp' },
    { name: 'Tretinoin Combo', category: 'Skin Care', desc: 'A dermatologist-backed formula that brightens skin, improves texture, and reduces hyperpigmentation while supporting cellular turnover.', slug: 'tretinoin-combo', image: '/images/landing-page/personalized-treatments-section/tretinoin-combo.webp' },
    { name: 'Sildenafil Combo', category: 'Sexual Wellness', desc: 'Dual-action ED treatment combining fast-acting Sildenafil with long-acting Tadalafil for enhanced, reliable performance across a longer window.', slug: 'sildenafil-combo', image: '/images/landing-page/personalized-treatments-section/sildenafil-combo.webp' }
  ];

  const steps = [
    { 
      num: '1', 
      title: 'Choose Your Membership', 
      desc: 'Pick one of our three membership plans to unlock personalized care.', 
      cta: 'Get Started',
      image: '/images/landing-page/how-it-works-section/membership.webp',
      theme: 'dark'
    },
    { 
      num: '2', 
      title: 'Complete your health intake', 
      desc: 'Answer a few simple, clinically guided questions to help us understand your needs.', 
      cta: 'Get a Diagnosis',
      image: '/images/landing-page/how-it-works-section/quiz.webp',
      theme: 'orange'
    },
    { 
      num: '3', 
      title: 'Get Your Doctor-Approved Plan', 
      desc: 'Our licensed providers review your quiz and recommend the right prescription for you.', 
      cta: 'Talk to Expert',
      image: '/images/landing-page/how-it-works-section/plan.webp',
      theme: 'light'
    }
  ];

  const trustPoints = [
    { image: '/images/landing-page/healthcare-you-can-trust-section/1.webp', title: 'Licensed medical providers', desc: 'Our doctors are board-certified in their specialties.' },
    { image: '/images/landing-page/healthcare-you-can-trust-section/2.webp', title: 'Secure telehealth platform', desc: 'Every treatment is backed by medical research.' },
    { image: '/images/landing-page/healthcare-you-can-trust-section/3.webp', title: 'FDA-regulated partner pharmacies', desc: 'Safe, compliant, and discreet.' },
    { image: '/images/landing-page/healthcare-you-can-trust-section/4.webp', title: 'Personalized treatment plans', desc: 'No generic answers. Just what\'s right for you.' }
  ];

  const testimonials = [
    { name: 'Arjun', age: 41, rating: 5, image: '/images/landing-page/real-people-real-result-section/1.webp', quote: 'I was nervous about seeking help, but Oval made it simple and discreet. My confidence is back.' },
    { name: 'Meera', age: 39, rating: 5, image: '/images/landing-page/real-people-real-result-section/2.webp', quote: 'My energy levels and focus improved within weeks. It feels good to finally have a solution tailored to me.' },
    { name: 'Rahul', age: 34, rating: 5, image: '/images/landing-page/real-people-real-result-section/3.webp', quote: 'I started my Oval membership 4 months ago. The treatment plan has already slowed my hair loss and I\'m seeing new growth.' },
    { name: 'Diego', age: 28, rating: 5, image: '/images/landing-page/real-people-real-result-section/4.webp', quote: 'Oval fit perfectly into my routine. The check-ins are quick, and I finally feel in control of my health.' }
  ];

  const faqs = [
    { q: 'How does the membership work?', a: 'Choose a plan, complete a brief health quiz, and our providers guide your prescription plan and follow-ups.' },
    { q: 'Do I need a prescription?', a: 'If your treatment requires one, our licensed providers handle it after your evaluation.' },
    { q: 'How does the health quiz work?', a: 'A few clinically guided questions to understand your needs and medical history.' },
    { q: "Are Oval's products safe?", a: 'Yes, we use clinically proven ingredients and FDA-regulated partner pharmacies.' },
    { q: 'Is delivery discreet?', a: 'Absolutely. Packaging is plain and privacy-safe.' },
    { q: 'Can I cancel my membership?', a: 'Yes, you can cancel anytime from your account settings.' },
    { q: 'Who are the providers?', a: 'Board-certified physicians and nurse practitioners across our specialties.' }
  ];

  return (
    <div className={styles.page}>
      {/* Hero Section with Video Background */}
      <section className={styles.hero}>
        <div className={styles.heroVideo}>
          <video
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
            poster="/videos/poster.jpg"
            src="/videos/oval-homepage-animation.mp4"
          />
        </div>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <div className={styles.heroGrain}></div>
          <h1>
            Healthcare for{' '}
            <span className={styles.heroGlassText}>
              hair, skin, sexual wellness, hormones, weight support <span className={styles.orangeText}>and beyond.</span>
            </span>
          </h1>
          <p>Discover Oval's modern approach to personalized telemedicine.</p>
          <Link to="/login" className={styles.heroBtn}>Get Started</Link>
        </div>
      </section>

      {/* Health Journey Section */}
      <section className={styles.journeySection}>
        <div className={styles.journeyCard}>
          <div className={styles.journeyContent}>
            <span className={styles.journeyTag}>
              <span className={styles.tagIcon}>★</span>
              Membership Benefits
            </span>
            <h2>Your health journey starts with <span className={styles.journeyHighlight}>our membership</span></h2>
            <p className={styles.journeyIntro}>
              Before you can access our prescription treatments, you'll become an <span className={styles.journeyAccent}>Oval Member</span>. Choose a plan, complete your health quiz, and get your <strong>personalized prescription</strong>, all from home.
            </p>

            {/* Steps */}
            <div className={styles.journeySteps}>
              <div className={styles.journeyStep}>
                <div className={styles.stepNumber}>1</div>
                <div className={styles.stepText}>
                  <strong>Choose Your Plan</strong>
                  <span>Select the membership that fits your needs</span>
                </div>
              </div>
              <div className={styles.journeyStep}>
                <div className={styles.stepNumber}>2</div>
                <div className={styles.stepText}>
                  <strong>Complete Health Quiz</strong>
                  <span>Quick assessment by licensed physicians</span>
                </div>
              </div>
              <div className={styles.journeyStep}>
                <div className={styles.stepNumber}>3</div>
                <div className={styles.stepText}>
                  <strong>Get Your Prescription</strong>
                  <span>Delivered to your door</span>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <Link to="/login" className={styles.journeyBtn}>
              Start Your Journey →
            </Link>
          </div>
          <div className={styles.journeyImage}>
            <video
              className={styles.journeyImageImg}
              src="/images/landing-page/cta-video.mp4"
              poster="/images/landing-page/cta-video-poster.webp"
              autoPlay
              muted
              loop
              playsInline
              aria-hidden="true"
            />
          </div>
        </div>
      </section>

      {/* Prescription Care Section */}
      <section className={styles.solutionsSection}>
        <div className={styles.container}>
          <div className={styles.solutionsHeader}>
            <div className={styles.solutionsHeaderText}>
              <span className={styles.solutionsTag}>
                <span className={styles.tagIcon}>⚕</span>
                Expert Care
              </span>
              <h2>Doctor-trusted solutions<br />for <span className={styles.solutionsHighlight}>every concern</span></h2>
              <p>From hair health to hormone balance, our doctors create a plan that's <strong>uniquely yours</strong>.</p>
            </div>
            <Link to="/login" className={styles.learnMoreBtn}>
              Learn More
              <span className={styles.btnArrow}>→</span>
            </Link>
          </div>
          <div className={styles.concernsGlassBox}>
            <div className={styles.concernsGlassGrain}></div>
            <div className={styles.concernsGrid}>
              <AuthLink to="/categories/hair-health" className={styles.concernCard}>
                <div className={styles.concernImageBox}>
                  <img loading="lazy" src="/images/landing-page/doctor-trusted-solutions-section/hair-health.webp" alt="Hair Health" className={styles.concernImage} />
                </div>
                <div className={styles.concernOverlay}>
                  <div className={styles.concernGrain}></div>
                  <h3>Hair Health</h3>
                  <p>Regrow, strengthen, and protect your hair.</p>
                </div>
              </AuthLink>
              <AuthLink to="/categories/skin-confidence" className={styles.concernCard}>
                <div className={styles.concernImageBox}>
                  <img loading="lazy" src="/images/landing-page/doctor-trusted-solutions-section/skin-confidence.webp" alt="Skin Confidence" className={styles.concernImage} />
                </div>
                <div className={styles.concernOverlay}>
                  <div className={styles.concernGrain}></div>
                  <h3>Skin Confidence</h3>
                  <p>Get clear, healthy, glowing skin.</p>
                </div>
              </AuthLink>
              <AuthLink to="/categories/sexual-wellness" className={styles.concernCard}>
                <div className={styles.concernImageBox}>
                  <img loading="lazy" src="/images/landing-page/doctor-trusted-solutions-section/sexual-wellness.webp" alt="Sexual Wellness" className={styles.concernImage} />
                </div>
                <div className={styles.concernOverlay}>
                  <div className={styles.concernGrain}></div>
                  <h3>Sexual Wellness</h3>
                  <p>Support performance and confidence.</p>
                </div>
              </AuthLink>
              <AuthLink to="/categories/hormone-balance" className={styles.concernCard}>
                <div className={styles.concernImageBox}>
                  <img loading="lazy" src="/images/landing-page/doctor-trusted-solutions-section/hormone-balance.webp" alt="Vitality" className={styles.concernImage} />
                </div>
                <div className={styles.concernOverlay}>
                  <div className={styles.concernGrain}></div>
                  <h3>Vitality</h3>
                  <p>Restore energy, mood and vitality.</p>
                </div>
              </AuthLink>
              <AuthLink to="/categories/weight-support" className={`${styles.concernCard} ${styles.concernCardWeight}`}>
                <div className={styles.concernImageBox}>
                  <img loading="lazy" src="/images/landing-page/doctor-trusted-solutions-section/weight-support.webp" alt="Weight Support" className={styles.concernImage} />
                </div>
                <div className={styles.concernOverlay}>
                  <div className={styles.concernGrain}></div>
                  <h3>Weight Support</h3>
                  <p>Clinically guided GLP-1 programs.</p>
                </div>
              </AuthLink>
              <AuthLink to="/categories/performance" className={`${styles.concernCard} ${styles.concernCardPerformance}`}>
                <div className={styles.concernImageBox}>
                  <img loading="lazy" src="/images/landing-page/doctor-trusted-solutions-section/performance.webp" alt="Performance" className={styles.concernImage} />
                </div>
                <div className={styles.concernOverlay}>
                  <div className={styles.concernGrain}></div>
                  <h3>Performance</h3>
                  <p>Optimize strength, energy, and vitality.</p>
                </div>
              </AuthLink>
            </div>
          </div>
        </div>
      </section>
      {/* Treatment Catalogue CTA */}
      <section className={styles.catalogCtaSection}>
        <div className={styles.catalogCtaContainer}>
          <Link to="/treatments" className={styles.catalogCtaBtn}>
            Treatment Catalogue
            <span className={styles.btnArrow}>→</span>
          </Link>
        </div>
      </section>

      {/* Personalized Treatments */}
      <section className={styles.treatmentsSection}>
        <div className={styles.container}>
          <h2>Personalized Treatments,<br /><span className={styles.glassText3D}>Backed by Experts</span></h2>
          <p>FDA-approved medications prescribed by licensed physicians</p>
        </div>
        <div className={styles.carouselWrapper}>
          <div className={styles.carouselTrack}>
            {/* First set of treatments */}
            {treatments.map((treatment) => (
              <Link key={treatment.slug} to="/login" className={styles.treatmentCard}>
                <div className={styles.treatmentImageBox}>
                  <img loading="lazy" src={treatment.image} alt={treatment.name} className={styles.treatmentImage} />
                </div>
                <div className={styles.treatmentTextBox}>
                  <span className={`${styles.treatmentCategory} ${getCategoryClass(treatment.category)}`}>{treatment.category}</span>
                  <h3>{treatment.name}</h3>
                  <p>{treatment.desc}</p>
                </div>
              </Link>
            ))}
            {/* Duplicate for seamless loop */}
            {treatments.map((treatment) => (
              <Link key={`${treatment.slug}-dup`} to="/login" className={styles.treatmentCard}>
                <div className={styles.treatmentImageBox}>
                  <img loading="lazy" src={treatment.image} alt={treatment.name} className={styles.treatmentImage} />
                </div>
                <div className={styles.treatmentTextBox}>
                  <span className={`${styles.treatmentCategory} ${getCategoryClass(treatment.category)}`}>{treatment.category}</span>
                  <h3>{treatment.name}</h3>
                  <p>{treatment.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Oval */}
      <section className={styles.whySection}>
        <div className={styles.container}>
          <div className={styles.whyGlassBox}>
          <div className={styles.whyContent}>
            <span className={styles.whyTag}>
              <span className={styles.tagIcon}>◆</span>
              Our Promise
            </span>
            <h2>Why Choose <span className={styles.gradientText}>Oval</span>?</h2>

            {/* Feature Cards Grid */}
            <div className={styles.whyFeatures}>
              <div className={styles.whyFeatureCard}>
                <div className={styles.featureIcon}>
                  <span>🩺</span>
                </div>
                <div className={styles.featureContent}>
                  <h4>Board-Certified Providers</h4>
                  <p>Access <strong>personalized treatments</strong> through state-licensed physicians.</p>
                </div>
              </div>

              <div className={styles.whyFeatureCard}>
                <div className={styles.featureIcon}>
                  <span>💊</span>
                </div>
                <div className={styles.featureContent}>
                  <h4>FDA-Regulated Pharmacies</h4>
                  <p>Medications dispensed through licensed <strong>503A pharmacies</strong>.</p>
                </div>
              </div>

              <div className={styles.whyFeatureCard}>
                <div className={styles.featureIcon}>
                  <span>💰</span>
                </div>
                <div className={styles.featureContent}>
                  <h4>Transparent Pricing</h4>
                  <p>No hidden fees. Revenue from <span className={styles.highlightText}>membership</span>, not markups.</p>
                </div>
              </div>
            </div>

            {/* Highlight Box */}
            <div className={styles.whyHighlightBox}>
              <div className={styles.highlightIcon}>✓</div>
              <p>All prescriptions are issued only after a <strong>licensed physician</strong> completes a clinical evaluation.</p>
            </div>
          </div>

          {/* Right Column - Image + Stats */}
          <div className={styles.whyRightColumn}>
            <div className={styles.whyImage}>
              <img
                src="/images/why-choose-oval.webp"
                alt="Woman with curly hair"
                className={styles.whyImageImg}
              />
            </div>

            {/* Stats Glass Box */}
            <div className={styles.whyStats}>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>100%</span>
                <span className={styles.statLabel}>Licensed Providers</span>
              </div>
              <div className={styles.statDivider}></div>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>8,000%</span>
                <span className={styles.statLabel}>Less<br />Markup</span>
              </div>
              <div className={styles.statDivider}></div>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>24/7</span>
                <span className={styles.statLabel}>Support Access</span>
              </div>
            </div>
          </div>
          </div>

          {/* CTA Section - Outside glass box */}
          <div className={styles.whyCta}>
            <div className={styles.whyCtaText}>
              <h3>Take Back Control of Your Health.</h3>
              <p>Join <span className={styles.gradientText}>Oval</span> today and experience healthcare that <strong>respects you</strong>.</p>
            </div>
            <Link to="/login" className={styles.whyCtaBtn}>Get Started</Link>
          </div>
        </div>
      </section>

      {/* Doctor-Trusted Solutions */}

      {/* Testimonials */}
      <section className={styles.testimonialsSection}>
        <div className={styles.container}>
          <div className={styles.testimonialsHeader}>
            <div className={styles.testimonialsTitle}>
              <h2>Real people, <span>Real results</span></h2>
              <p>Here's what our community is saying about their Oval care experience.</p>
            </div>
            <div className={styles.testimonialsNav}>
              <button className={styles.navBtn} aria-label="Previous">
                <span>‹</span>
              </button>
              <button className={styles.navBtn} aria-label="Next">
                <span>›</span>
              </button>
            </div>
          </div>
          <div className={styles.testimonialsGrid} ref={testimonialsRef}>
            {testimonials.map((t, idx) => (
              <div key={idx} className={styles.testimonialCard}>
                <div className={styles.testimonialImageBox}>
                  <img loading="lazy" src={t.image} alt={t.name} className={styles.testimonialImage} />
                </div>
                <div className={styles.testimonialQuoteBox}>
                  <p className={styles.testimonialQuote}>"{t.quote}"</p>
                  <div className={styles.testimonialFooter}>
                    <div className={styles.testimonialStars}>
                      {'★'.repeat(Math.floor(t.rating))}
                      {t.rating % 1 !== 0 && '☆'}
                    </div>
                    <span className={styles.testimonialName}>– {t.name}, {t.age}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className={styles.howSection}>
        <div className={styles.container}>
          <h2>How It Works</h2>
          <p className={styles.sectionSubtitle}>In just 3 easy steps, get your doctor-approved plan.</p>
          <div className={styles.stepsGrid} ref={stepsRef}>
            {steps.map((step) => (
              <div key={step.num} className={`${styles.stepCard} ${styles[`stepCard${step.theme.charAt(0).toUpperCase() + step.theme.slice(1)}`]}`}>
                <div className={styles.stepImageWrapper}>
                  <img loading="lazy" src={step.image} alt={step.title} className={styles.stepImage} />
                </div>
                <div className={styles.stepContentBox}>
                  <div className={styles.stepHeader}>
                    <span className={styles.stepNum}>{step.num}</span>
                  </div>
                  <h3 className={styles.stepTitle}>{step.title}</h3>
                  <p className={styles.stepDesc}>{step.desc}</p>
                  <Link to="/login" className={styles.stepCta}>
                    <span>{step.cta}</span>
                    <span className={styles.stepArrow}>→</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.stepsDots}>
            {steps.map((_, idx) => (
              <button
                key={idx}
                className={`${styles.stepDot} ${idx === activeStep ? styles.stepDotActive : ''}`}
                aria-label={`Step ${idx + 1}`}
                onClick={() => {
                  const container = stepsRef.current;
                  if (!container) return;
                  const target = container.children[idx];
                  if (!target) return;
                  const containerRect = container.getBoundingClientRect();
                  const targetRect = target.getBoundingClientRect();
                  const offset = targetRect.left - containerRect.left + container.scrollLeft
                    - (containerRect.width / 2) + (targetRect.width / 2);
                  container.scrollTo({ left: offset, behavior: 'smooth' });
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Healthcare You Can Trust */}
      <section className={styles.trustSection}>
        <div className={styles.container}>
          <h2>Healthcare <span style={{ color: '#f97316' }}>you can trust</span></h2>
          <p className={styles.sectionSubtitle}>Board-certified doctors, licensed pharmacies, and FDA-approved treatments — every step of the way.</p>
          <div className={styles.trustGrid}>
            {trustPoints.map((point, idx) => (
              <div key={idx} className={styles.trustItem}>
                <div className={styles.trustGrain}></div>
                <div className={styles.trustIconBox}>
                  <img loading="lazy" src={point.image} alt={point.title} className={styles.trustIcon} />
                </div>
                <div className={styles.trustText}>
                  <h3>{point.title}</h3>
                  <p>{point.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <div className={styles.container}>
          <div className={styles.faqGrid}>
            <div className={styles.faqLeft}>
              <div className={styles.faqCard}>
                <img loading="lazy" src="/images/landing-page/faq-products.webp" alt="Oval Products" className={styles.faqImage} />
              </div>
            </div>
            <div className={styles.faqRight}>
              {faqs.map((faq, idx) => (
                <details key={idx} className={styles.faqItem}>
                  <summary>
                    <span>{faq.q}</span>
                    <span className={styles.faqIcon}>+</span>
                  </summary>
                  <p>{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};
export default HomePage;
